import { useState, useEffect, useCallback } from "react"
import { cn } from "../../lib/utils"
import { Check, X } from "lucide-react"

interface ToastData {
  id: number
  message: string
  type: "success" | "error"
}

let toastId = 0
let addToastFn: ((msg: string, type?: "success" | "error") => void) | null = null

export function toast(message: string, type: "success" | "error" = "success") {
  addToastFn?.(message, type)
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<ToastData[]>([])

  const addToast = useCallback((message: string, type: "success" | "error" = "success") => {
    const id = ++toastId
    setToasts((prev) => [...prev, { id, message, type }])
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 3000)
  }, [])

  useEffect(() => {
    addToastFn = addToast
    return () => {
      addToastFn = null
    }
  }, [addToast])

  if (toasts.length === 0) return null

  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={cn(
            "flex items-center gap-2 rounded-lg border px-4 py-3 shadow-lg text-sm",
            "animate-in slide-in-from-top-2 fade-in duration-200",
            t.type === "success" &&
              "bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200",
            t.type === "error" &&
              "bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200",
          )}
        >
          {t.type === "success" ? (
            <Check className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400" />
          ) : (
            <X className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
          )}
          {t.message}
        </div>
      ))}
    </div>
  )
}
